import { useMemo, useState, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import { CoreEnumPickerView } from './CoreEnumPickerView';
import {
  buildCoreEnumPickerOptions,
  findSelectedCoreEnum,
  optionPreview,
  resolveSelectedCoreEnumOption,
} from './coreEnumPickerSelection';

export interface CoreEnumPickerProps {
  /** Selected Core Enum id, or null for the "none / default" choice. */
  value: string | null;
  onChange: (enumId: string | null) => void;
  /** Show a first option that clears the selection. */
  allowNone?: boolean;
  noneLabel?: string;
  noneDisplay?: string;
  /** Extra human explanation under the picker. */
  helpText?: ReactNode;
  disabled?: boolean;
  /** Override create-modal description for domain-specific flows. */
  createDescription?: ReactNode;
  optionNamePlaceholder?: string;
  className?: string;
}

/**
 * Shared searchable Core Enum picker with inline create/edit via
 * {@link CoreEnumModal}. Use anywhere a flow needs to bind to an enum id
 * (task outcomes, classifiers, tags, etc.).
 */
export function CoreEnumPicker({
  value,
  onChange,
  allowNone = true,
  noneLabel,
  noneDisplay,
  helpText,
  disabled = false,
  createDescription,
  optionNamePlaceholder,
  className,
}: CoreEnumPickerProps) {
  const gt = useGT();
  const { data: enumsResponse, isLoading } = useCoreEnums(
    { first: 100, includeOptions: true },
    { staleTime: 30_000 },
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] =
    useState<Parameters<typeof CoreEnumPickerView>[0]['editTarget']>(null);
  const emptyOptionsLabel = gt('No options yet', {
    context: 'core enum picker preview when an enum has no options',
  });
  const options = useMemo(
    () =>
      buildCoreEnumPickerOptions({
        coreEnums: enumsResponse?.edges ?? [],
        emptyOptionsLabel,
        allowNone,
        noneLabel:
          noneLabel ??
          gt('None', { context: 'core enum picker none / clear option' }),
        noneDisplay:
          noneDisplay ??
          gt('None', { context: 'core enum picker none short display' }),
      }),
    [
      allowNone,
      emptyOptionsLabel,
      enumsResponse?.edges,
      gt,
      noneDisplay,
      noneLabel,
    ],
  );
  const selectedEnum = findSelectedCoreEnum(enumsResponse?.edges, value);
  const selectedPreview = selectedEnum
    ? optionPreview(selectedEnum, emptyOptionsLabel)
    : null;

  return (
    <CoreEnumPickerView
      className={className ?? 'space-y-2'}
      options={options}
      selected={resolveSelectedCoreEnumOption(options, value, allowNone)}
      selectedEnum={selectedEnum}
      selectedPreview={selectedPreview}
      helpText={helpText}
      disabled={disabled}
      isLoading={isLoading}
      placeholder={
        isLoading
          ? gt('Loading enums…', {
              context: 'core enum picker loading placeholder',
            })
          : gt('Search Core Enums…', {
              context: 'core enum picker search placeholder',
            })
      }
      searchPlaceholder={gt('Search…', {
        context: 'search box in the core enum picker',
      })}
      createOpen={createOpen}
      editTarget={editTarget}
      createDescription={createDescription}
      optionNamePlaceholder={optionNamePlaceholder}
      onChange={onChange}
      onCreate={() => {
        setEditTarget(null);
        setCreateOpen(true);
      }}
      onEdit={setEditTarget}
      onCloseModal={() => {
        setCreateOpen(false);
        setEditTarget(null);
      }}
      onSaved={(saved) => onChange(saved.id)}
    />
  );
}
